import React, { useEffect } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { getComponent } from '../components/componentRegistry.ts';
import { useSurfaceContext } from '../context/SurfaceContext.tsx';
import { registerComponentRenderer } from '../hooks/useChildren.tsx';

type ComponentRendererProps = {
	componentId: string;
	visited?: Set<string>;
};

const styles = cssMap({
	errorFallback: {
		color: token('color.text.danger'),
		border: `${token('border.width')} solid ${token('color.border.danger')}`,
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		marginTop: token('space.050'),
		marginRight: token('space.050'),
		marginBottom: token('space.050'),
		marginLeft: token('space.050'),
	},
});

export const A2UIComponentRenderer = ({
	componentId,
	visited = new Set(),
}: ComponentRendererProps): React.JSX.Element | null => {
	const { state } = useSurfaceContext();
	const { componentMap } = state;

	// Cycle detection
	if (visited.has(componentId)) {
		return null;
	}

	const definition = componentMap.get(componentId);
	if (!definition) {
		return null;
	}

	const Component = getComponent(definition.type);
	if (!Component) {
		return null;
	}

	const nextVisited = new Set(visited);
	nextVisited.add(componentId);

	return (
		<A2UIComponentErrorBoundary componentId={componentId} type={definition.type}>
			<Component definition={definition} visited={nextVisited} />
		</A2UIComponentErrorBoundary>
	);
};

// Register self for useChildren hook
registerComponentRenderer(A2UIComponentRenderer);

// Per-component error boundary
type ErrorBoundaryProps = {
	children: React.ReactNode;
	componentId: string;
	type: string;
};

const A2UIComponentErrorBoundary = ({ children, componentId, type }: ErrorBoundaryProps) => {
	return (
		<ErrorBoundary
			fallbackRender={({ error }) => {
				const errorMessage = error instanceof Error ? error.message : String(error);
				const renderErrorTitle = `${type} (${componentId}) failed to render:`;

				return (
					<Box xcss={cx(styles.errorFallback)}>
						<Text as="span" weight="bold">
							{renderErrorTitle}
						</Text>{' '}
						{errorMessage}
					</Box>
				);
			}}
			resetKeys={[componentId, type]}
		>
			{children}
		</ErrorBoundary>
	);
};

// Root tree renderer
type TreeBuilderProps = {
	rootId: string;
};

export const TreeBuilder = ({ rootId }: TreeBuilderProps): React.JSX.Element | null => {
	return <A2UIComponentRenderer componentId={rootId} />;
};

// Hook to ensure component renderer is registered (call once in Surface)
export function useTreeBuilderRegistration(): void {
	useEffect(() => {
		registerComponentRenderer(A2UIComponentRenderer);
	}, []);
}
